import * as React from "react";
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Grow from "@mui/material/Grow";
import LanguageToggleButton from "../../shared/toggleLanguage";
import berglogo from "../../../assets/images/bergLogo.webp";
import { useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [scrolled, setScrolled] = useState(false); 
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const locale = i18n.language || localStorage.getItem("language") || "en";
  const theme = useTheme();
  const location = useLocation();

  const pages = useMemo(
    () => [
      { label: t("navbar.Home"), path: `/${locale}` },
      { label: t("navbar.About"), path: `/${locale}/about` },
      { label: t("navbar.Services"), path: `/${locale}/services` },
      { label: t("navbar.Contact"), path: `/${locale}/contact` },
    ],
    [locale, t]
  );

  const activePage = useMemo(() => {
    const currentPage = pages.find((page) => location.pathname === page.path);
    return currentPage?.label || "";
  }, [location.pathname, pages]);

  const handleHomeRoute = () => {
    navigate(`/${locale}`);
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handlePageChange = (page) => {
    navigate(page.path);
    handleCloseNavMenu();
  };

  // Scroll event listener to toggle shadow
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "white",
        top: 1,
        zIndex: 1800,
        py: 0.5,
        boxShadow: scrolled
        ? "0px 4px 6px rgba(211, 211, 211, 0.5)" // Light gray shadow
        : "none", // Add shadow when scrolled
        transition: "box-shadow 0.3s ease-in-out",
      }}
      elevation={0}
      
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-end",
              flexDirection: "row-reverse",
              cursor: "pointer",
            }}
            onClick={handleHomeRoute}
          >
            <Box
              component="img"
              src={berglogo}
              loading="lazy"
              alt="berglogo"
              sx={{
                paddingLeft: locale === "ar" ? "15px" : "",
                paddingRight: locale === "en" ? "15px" : "",
                width: {
                  xs: "180px",
                  sm: "180px",
                  md: "180px",
                },
                height: {
                  sm: "70px",
                  md: "85px",
                },
                objectFit: "contain",
              }}
            />
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              alignItems: "center",
              gap: "15px",
            }}
          >
            {pages.map((page) => (
              <Button
                key={page.label}
                onClick={() => handlePageChange(page)}
                sx={{
                  textTransform: "capitalize",
                  color:
                    activePage === page.label
                      ? theme.palette.primary.negative
                      : theme.palette.primary.disabled,
                  backgroundColor:
                    activePage === page.label ? "#0A1F52" : "none",
                  display: "block",
                  fontSize: { md: "12px", lg: "16px" },
                  fontWeight: activePage === page.label ? "500" : "400",
                  borderRadius: "12px",
                  padding: activePage === page.label ? "8px 24px" : "",
                }}
              >
                {page.label}
              </Button>
            ))}
          </Box>
          <Box sx={{ flex: { xs: 1, md: 0 } }}></Box>
          <LanguageToggleButton />

          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="#000"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              TransitionComponent={Grow}
              sx={{
                display: { xs: "block", md: "none" },
                "& .MuiMenu-paper": {
                  backgroundColor: "#f0f8ff",
                  borderRadius: "8px",
                  width: "100%",
                  mt: "8px",
                },
                "& .MuiMenu-list": {
                  backgroundColor: "white",
                },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.label}
                  onClick={() => handlePageChange(page)}
                  sx={{
                    backgroundColor:
                      activePage === page.label ? "#e7eef5" : "white",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "18px",
                      color: activePage === page.label ? "black" : "#3C3E44",
                      fontWeight: activePage === page.label ? "700" : "500",
                    }}
                  >
                    {page.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;
